import { createFileRoute, Outlet, redirect, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AppShell } from "@/components/app-shell";
import { OnboardingFlow } from "@/components/onboarding-flow";
import { useAuth } from "@/lib/auth-context";
import { useAccess } from "@/hooks/use-access";

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw redirect({ to: "/auth" });
    return { userId: data.session.user.id };
  },
  component: AuthedLayout,
});

function AuthedLayout() {
  const { user, loading } = useAuth();
  const [profileChecked, setProfileChecked] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const access = useAccess();
  const location = useLocation();
  const navigate = useNavigate();

  async function refresh() {
    if (!user) return;
    const { data } = await supabase
      .from("profiles")
      .select("onboarding_completed_at")
      .eq("id", user.id)
      .maybeSingle();
    setOnboardingComplete(!!data?.onboarding_completed_at);
    setProfileChecked(true);
  }

  useEffect(() => {
    if (user) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Route enforcement: if no access AND not already on paywall, redirect.
  useEffect(() => {
    if (
      profileChecked &&
      onboardingComplete &&
      !access.loading &&
      !access.hasAccess &&
      location.pathname !== "/paywall"
    ) {
      navigate({ to: "/paywall", replace: true });
    }
  }, [profileChecked, onboardingComplete, access.loading, access.hasAccess, location.pathname, navigate]);

  if (loading || !user || !profileChecked || access.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm">Loading your boardroom…</div>
      </div>
    );
  }

  if (!onboardingComplete) {
    return <OnboardingFlow onDone={() => refresh()} />;
  }

  // Paywall renders without the shell.
  if (location.pathname === "/paywall") {
    return <Outlet />;
  }

  if (!access.hasAccess) {
    // Redirecting via effect above; render a placeholder to avoid flash.
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm">Redirecting…</div>
      </div>
    );
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
